import React from 'react';

import type { AgentSummaryRow } from '@/pages/analytics/types';
import {
  COACHING_SCORE_LTE,
  SCORE_BAND_EPSILON,
  SCORE_BAND_MEETS_MIN,
} from '@/pages/analytics/utils/scoreBands';
import { cn } from '@/utils';

export type AgentsQuickFilter = 'all' | 'best' | 'coaching';

interface AgentsQuickFilterChipsProps {
  totalAgents: number;
  agents: AgentSummaryRow[];
  active: AgentsQuickFilter;
  onChange: (next: AgentsQuickFilter) => void;
}

// Per-chip count-badge tone. Used for the small pill next to each label,
// matching QcStatsTabs' tone-coded count badges.
const CHIP_BADGE_ACTIVE: Record<AgentsQuickFilter, string> = {
  all: 'bg-secondary text-secondary-foreground',
  best: 'bg-positive text-positive-foreground',
  coaching: 'bg-attention text-attention-foreground',
};

/**
 * Maps a quick filter chip to the (score_gte, score_lte) query params for
 * /analytics/agents/list. Centralized so URL state and chip display stay
 * consistent.
 */
export const quickFilterToScoreRange = (
  filter: AgentsQuickFilter,
): { scoreGte?: number; scoreLte?: number } => {
  switch (filter) {
    case 'best':
      return { scoreGte: SCORE_BAND_MEETS_MIN + SCORE_BAND_EPSILON };
    case 'coaching':
      return { scoreLte: COACHING_SCORE_LTE };
    case 'all':
    default:
      return {};
  }
};

export const AgentsQuickFilterChips: React.FC<AgentsQuickFilterChipsProps> = ({
  totalAgents,
  agents,
  active,
  onChange,
}) => {
  const bestCount = agents.filter(a => a.avg_score > SCORE_BAND_MEETS_MIN).length;
  const coachingCount = agents.filter(a => a.avg_score < SCORE_BAND_MEETS_MIN).length;

  const chips: Array<{ value: AgentsQuickFilter; label: string; count: number }> = [
    { value: 'all', label: 'All', count: totalAgents },
    { value: 'best', label: 'Best Performing', count: bestCount },
    { value: 'coaching', label: 'Unsatisfactory', count: coachingCount },
  ];

  return (
    <div className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted/60 p-1">
      {chips.map(chip => {
        const isActive = chip.value === active;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            aria-pressed={isActive}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
            )}
          >
            <span>{chip.label}</span>
            <span
              className={cn(
                'inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-md',
                isActive ? CHIP_BADGE_ACTIVE[chip.value] : 'bg-muted text-muted-foreground',
              )}
            >
              {chip.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};
